import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useAuth } from "react-oidc-context";
import { isPriceChanged, priceChangedTotal } from "../../api/cart";
import {
  collectAreas,
  collectCustomer,
  collectCustomers,
  collectPayment,
  collectToday,
  matchCustomers,
  type CollectCustomer,
} from "../../api/collect";
import {
  listPriceWithGST,
  saleReceipts,
  type RechargeResult,
} from "../../api/subscriberRecharge";
import { useIdentity } from "../../auth/useIdentity";
import { CashCodeDialog } from "../../components/CashCodeDialog";
import { UpiPayQR, usePayOptions } from "../payments/PayBlock";

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));
const rs = (n: number) =>
  `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

/**
 * The door collector's phone page (owner 2026-11-02, radius-wiki
 * door-collection-procedure.md): no console, only this. Pick an area, find the
 * customer (name, login, card number or the last 4 digits), take cash or UPI,
 * and see today's total for the cash handover. The server keeps the collector
 * to its own areas and never sends a password or a full mobile.
 */
export function CollectPage() {
  const identity = useIdentity();
  const auth = useAuth();
  const ns = identity.namespace ?? "";
  const [area, setArea] = useState("");
  const [query, setQuery] = useState("");
  const [customer, setCustomer] = useState<CollectCustomer | null>(null);

  const areas = useQuery({
    queryKey: ["collect", "areas", ns],
    enabled: Boolean(identity.token && ns),
    queryFn: ({ signal }) => collectAreas(identity, ns, signal),
  });
  const chosen = area || (areas.data?.length === 1 ? areas.data[0]!.id : "");
  const customers = useQuery({
    queryKey: ["collect", "customers", ns, chosen],
    enabled: Boolean(identity.token && ns && chosen),
    queryFn: ({ signal }) => collectCustomers(identity, ns, chosen, signal),
  });
  const today = useQuery({
    queryKey: ["collect", "today", ns],
    enabled: Boolean(identity.token && ns),
    queryFn: ({ signal }) => collectToday(identity, ns, signal),
  });

  return (
    <Box
      sx={{
        maxWidth: 560,
        mx: "auto",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h6">Collection</Typography>
        <Button size="small" onClick={() => void auth.signoutRedirect()}>
          Sign out
        </Button>
      </Stack>
      {today.data && (
        <Alert severity="info" sx={{ py: 0 }}>
          Today: {today.data.collections.length} payment
          {today.data.collections.length === 1 ? "" : "s"}
          {Object.entries(today.data.totals).map(
            ([m, t]) => ` · ${m === "cash" ? "Cash" : "UPI"} ${rs(t)}`,
          )}
          {today.data.totals.cash ? ". Hand the cash over at the office." : ""}
        </Alert>
      )}

      {customer ? (
        <TakePayment
          ns={ns}
          customer={customer}
          onBack={() => {
            setCustomer(null);
          }}
        />
      ) : (
        <>
          {areas.error && (
            <Alert severity="error">{errText(areas.error)}</Alert>
          )}
          {areas.data && areas.data.length === 0 && (
            <Alert severity="warning">
              No area is given to you yet. Ask the office to give you your
              areas.
            </Alert>
          )}
          {areas.data && areas.data.length > 1 && (
            <TextField
              select
              size="small"
              label="Area"
              value={chosen}
              onChange={(e) => setArea(e.target.value)}
            >
              {areas.data.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          {chosen && (
            <TextField
              size="small"
              label="Find: name, card number or last 4 digits of mobile"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          )}
          {customers.error && (
            <Alert severity="error">{errText(customers.error)}</Alert>
          )}
          {customers.data && customers.data.length === 0 && (
            <Alert severity="info">No customers in this area yet.</Alert>
          )}
          {customers.data &&
            matchCustomers(customers.data, query).map((c) => (
              <Card key={c.accountid} variant="outlined">
                <CardActionArea onClick={() => setCustomer(c)}>
                  <CardContent sx={{ py: 1.25 }}>
                    <Typography variant="body1">
                      {c.name || c.username}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[
                        c.card_no && `Card ${c.card_no}`,
                        c.mobile,
                        c.expiry ? `till ${c.expiry}` : "no plan",
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
        </>
      )}
    </Box>
  );
}

function TakePayment({
  ns,
  customer,
  onBack,
}: {
  ns: string;
  customer: CollectCustomer;
  onBack: () => void;
}) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const detail = useQuery({
    queryKey: ["collect", "customer", ns, customer.accountid],
    enabled: Boolean(identity.token && ns),
    queryFn: ({ signal }) =>
      collectCustomer(identity, ns, customer.accountid, signal),
  });
  const payOptions = usePayOptions(ns, customer.accountid);
  const [groupname, setGroupname] = useState("");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<"cash" | "upi">("cash");
  const [reference, setReference] = useState("");
  const [card, setCard] = useState("");
  const [requestid, setRequestid] = useState(() => crypto.randomUUID());
  const [cashOpen, setCashOpen] = useState(false);
  const [done, setDone] = useState<RechargeResult | null>(null);
  const [notice, setNotice] = useState("");

  const plans = detail.data?.plans.filter((p) => p.pin === "") ?? [];
  const current = detail.data?.devices.find((d) => d.plan)?.plan ?? "";
  const plan = plans.find((p) => p.groupname === (groupname || current));
  const price = plan ? listPriceWithGST(plan) : null;
  if (plan && !groupname) setGroupname(plan.groupname);
  if (plan && amount === "" && price != null) setAmount(String(price));

  const pay = useMutation({
    mutationFn: (cashCode: string | undefined) =>
      collectPayment(identity, ns, customer.accountid, {
        groupname,
        amount: Number(amount),
        method,
        reference: method === "upi" ? reference.trim() : "",
        requestid,
        ...(card.trim() ? { card_no: card.trim() } : {}),
        ...(cashCode ? { cash_code: cashCode } : {}),
      }),
    onSuccess: (r) => {
      setDone(r);
      void qc.invalidateQueries({ queryKey: ["collect"] });
    },
    onError: (e) => {
      if (!isPriceChanged(e)) return;
      const now = priceChangedTotal(e);
      setNotice(
        `The price has changed${now != null ? `: the amount is now ${rs(now)}` : ""}. Collect that amount and try again.`,
      );
      if (now != null) setAmount(String(now));
      setRequestid(crypto.randomUUID());
    },
  });

  const amountOk =
    /^\d{1,6}(\.\d{1,2})?$/.test(amount.trim()) && Number(amount) > 0;
  const refOk =
    method === "cash" || /^[A-Za-z0-9]{6,40}$/.test(reference.trim());
  const ready = Boolean(plan) && amountOk && refOk && !pay.isPending;

  return (
    <Stack spacing={1.5}>
      <Button size="small" sx={{ alignSelf: "flex-start" }} onClick={onBack}>
        ← Back to the list
      </Button>
      <Typography variant="h6">{customer.name || customer.username}</Typography>
      <Typography variant="body2" color="text.secondary">
        {[
          customer.card_no && `Card ${customer.card_no}`,
          customer.mobile,
          customer.expiry ? `Plan till ${customer.expiry} IST` : "No plan now",
        ]
          .filter(Boolean)
          .join(" · ")}
      </Typography>
      {detail.error && <Alert severity="error">{errText(detail.error)}</Alert>}
      {done ? (
        <>
          <Alert severity="success">
            {done.replayed ? "Already recorded" : "Paid"}: receipt{" "}
            {saleReceipts(done)
              .map((r) => r.receiptno)
              .join(", ")}
            {done.expiry
              ? `, plan till ${new Date(done.expiry).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} IST`
              : ""}
            .
          </Alert>
          <Button variant="contained" onClick={onBack}>
            Next customer
          </Button>
        </>
      ) : (
        detail.data && (
          <>
            {plans.length === 0 && (
              <Alert severity="warning">
                No plan can be given at the door. Ask the office.
              </Alert>
            )}
            {plans.length > 0 && (
              <TextField
                select
                size="small"
                label="Plan"
                value={groupname}
                onChange={(e) => {
                  setGroupname(e.target.value);
                  const p = plans.find((x) => x.groupname === e.target.value);
                  const v = p ? listPriceWithGST(p) : null;
                  setAmount(v != null ? String(v) : "");
                  setNotice("");
                }}
              >
                {plans.map((p) => (
                  <MenuItem key={p.groupname} value={p.groupname}>
                    {p.groupname}
                    {p.groupname === current ? " (current)" : ""}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              size="small"
              label="Amount collected (₹)"
              inputProps={{ inputMode: "decimal" }}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <RadioGroup
              row
              value={method}
              onChange={(e) => setMethod(e.target.value as "cash" | "upi")}
            >
              <FormControlLabel
                value="cash"
                control={<Radio size="small" />}
                label="Cash"
              />
              <FormControlLabel
                value="upi"
                control={<Radio size="small" />}
                label="UPI (show QR)"
              />
            </RadioGroup>
            {method === "upi" && (
              <>
                <UpiPayQR
                  options={payOptions.data}
                  amount={amountOk ? Number(amount) : null}
                  note="Internet service"
                />
                <TextField
                  size="small"
                  label="UPI reference (UTR) from the customer's phone"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                />
              </>
            )}
            <TextField
              size="small"
              label="Paper card number (optional)"
              value={card}
              placeholder={detail.data.card_no}
              onChange={(e) => setCard(e.target.value)}
            />
            {notice && <Alert severity="warning">{notice}</Alert>}
            {pay.error && !isPriceChanged(pay.error) && (
              <Alert severity="error">{errText(pay.error)}</Alert>
            )}
            <Button
              variant="contained"
              size="large"
              disabled={!ready}
              onClick={() =>
                method === "cash" ? setCashOpen(true) : pay.mutate(undefined)
              }
            >
              {pay.isPending
                ? "Saving…"
                : `Take ${amountOk ? rs(Number(amount)) : "payment"}`}
            </Button>
            <CashCodeDialog
              open={cashOpen}
              identity={identity}
              ns={ns}
              account={customer.accountid}
              amount={Number(amount)}
              saleRef={requestid}
              onCancel={() => setCashOpen(false)}
              onConfirm={(code) => {
                setCashOpen(false);
                pay.mutate(code);
              }}
            />
          </>
        )
      )}
    </Stack>
  );
}
